import type { ShallowRef } from 'vue';
import { useRuntimeConfig } from '#app';
import { DEFAULT_LOCALE } from '~/constants/i18n';
import { getLocaleMeta, localeFromRouteName, normalizeLocale, parseEnabledLocales } from '~/utils/i18n/locale';
import { buildLocalizedHeadLinks, isRawMessageKey, resolveSiteBase } from '~/utils/i18n/seo';

type CanonicalPathGetter = () => string | null | undefined;

// Per-app (i.e. per-request on the server) registry of the page-declared canonical. A getter, not a
// value: on the server the page's data may still be loading at setup time, and the head (where
// the getter is read) only resolves at render end — a one-shot copy would miss it.
const canonicalDeclarations = new WeakMap<object, ShallowRef<CanonicalPathGetter | null>>();

const useDeclaredCanonicalPath = (): ShallowRef<CanonicalPathGetter | null> => {
  const nuxtApp = useNuxtApp();
  let declared = canonicalDeclarations.get(nuxtApp);
  if (!declared) {
    declared = shallowRef<CanonicalPathGetter | null>(null);
    canonicalDeclarations.set(nuxtApp, declared);
  }
  return declared;
};

/**
 * Called by page SEO composables that emit their own canonical (usePropertySeo, useDeveloperSeo)
 * with its English (unprefixed) path, so useLocalizedSeo knows when the current URL is not the
 * canonical one (no alternates then). Released when the calling component unmounts.
 */
export const useCanonicalPathDeclaration = (englishPath: MaybeRefOrGetter<string | null | undefined>) => {
  const declared = useDeclaredCanonicalPath();
  const getter: CanonicalPathGetter = () => toValue(englishPath);
  declared.value = getter;
  onScopeDispose(() => {
    // The next page's setup may already have declared its own — only release ours.
    if (declared.value === getter) declared.value = null;
  });
};

/**
 * Site-wide locale SEO, called once from app.vue.
 *
 * - Localized routes (LOCALIZED_PAGES — their route names carry a `___<locale>` suffix) get
 *   `<link rel="canonical">` to the current locale's URL plus `<link rel="alternate" hreflang>`
 *   for `x-default` (English URL) and each ENABLED locale. Opted-out routes (portal, auth,
 *   staff, one-offs) emit none of these. Pages that compute their own canonical
 *   (usePropertySeo / useDeveloperSeo) declare it via useCanonicalPathDeclaration; when it is a
 *   different URL, no alternates are emitted for the current one.
 * - Non-English renders get translated site-wide description/OG defaults (nuxt.config app.head
 *   holds the English ones) and a matching `og:locale`. Pages' own useSeoMeta still wins.
 *
 * Everything here derives from the URL + runtime config only (never cookies), so it is safe on
 * SWR-cached pages.
 */
export const useLocalizedSeo = () => {
  const route = useRoute();
  const config = useRuntimeConfig();
  const { t, locale } = useI18n();

  const base = resolveSiteBase(config.public.appUrl as string | undefined);
  const enabled = computed(() => parseEnabledLocales(String(config.public.enabledLocales ?? '')));
  const currentLocale = computed(() => normalizeLocale(locale.value) ?? DEFAULT_LOCALE);
  const isDefaultLocale = computed(() => currentLocale.value === DEFAULT_LOCALE);
  const declaredCanonicalPath = useDeclaredCanonicalPath();

  const headLinks = computed(() => {
    const routeLocale = localeFromRouteName(route.name);
    if (!routeLocale) return [];
    const { canonical, alternates } = buildLocalizedHeadLinks({
      base,
      path: route.path,
      locale: routeLocale,
      enabled: enabled.value,
      canonicalPath: declaredCanonicalPath.value?.() ?? null,
    });
    return [
      { rel: 'canonical', href: canonical },
      ...alternates.map(({ hreflang, href }) => ({ rel: 'alternate', hreflang, href })),
    ];
  });

  useHead({ link: headLinks });

  // A missing translation falls back to the English default rather than publishing the key.
  const localizedOrDefault = (key: string): string | undefined => {
    const text = t(key);
    return isRawMessageKey(text) ? undefined : text;
  };

  // `undefined` leaves the English nuxt.config default in place (English output unchanged).
  useSeoMeta({
    description: () => (isDefaultLocale.value ? undefined : localizedOrDefault('shell.seo.description')),
    ogDescription: () => (isDefaultLocale.value ? undefined : localizedOrDefault('shell.seo.ogDescription')),
    ogImageAlt: () => (isDefaultLocale.value ? undefined : localizedOrDefault('shell.seo.ogImageAlt')),
    twitterDescription: () => (isDefaultLocale.value ? undefined : localizedOrDefault('shell.seo.twitterDescription')),
    ogLocale: () => (isDefaultLocale.value ? undefined : getLocaleMeta(currentLocale.value).language.replace('-', '_')),
  });

  return {
    headLinks,
  };
};
